
// ── MODAL / TOAST ────────────────────────────────────────────────────

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(', ');

// Where focus was when the dialog opened, so it can be put back on close.
// Without this, dismissing a modal drops focus to the top of the document and a
// keyboard user has to tab back to wherever they were.
let modalReturnFocus = null;

// Name the dialog from its own heading. Every modal in this app opens with an
// <h3>; aria-label is only the fallback for the handful that don't, since an
// unnamed dialog is announced as just "dialog".
function nameDialog(modal, box) {
  const heading = box.querySelector('h1, h2, h3, h4, .card-hd');
  if (heading) {
    if (!heading.id) heading.id = 'modalTitle';
    modal.removeAttribute('aria-label');
    modal.setAttribute('aria-labelledby', heading.id);
  } else {
    modal.removeAttribute('aria-labelledby');
    modal.setAttribute('aria-label', 'Dialog');
  }
}

// Keep Tab inside the dialog. Without it, tabbing walks out into the page behind
// — which is visually obscured, so focus simply disappears.
function trapTab(e) {
  if (e.key !== 'Tab') return;

  const box = document.getElementById('modalBox');
  const items = [...box.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null);

  if (!items.length) { e.preventDefault(); return; }

  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;

  if (e.shiftKey && (active === first || active === box)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

export function openModal(html, hideClose = false) {
  const modal = document.getElementById('modal');
  const box = document.getElementById('modalBox');

  modalReturnFocus = document.activeElement;

  box.innerHTML = (hideClose ? '' : '<button class="modal-close" onclick="closeModal()" aria-label="Close"><svg aria-hidden="true" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8"/></svg></button>') + html;
  modal.classList.add('show');
  document.body.style.overflow = 'hidden';

  nameDialog(modal, box);

  // Focus the dialog container rather than its first control. A screen reader
  // then announces the dialog and its heading from the top, and a confirmation
  // dialog never lands focus straight onto its destructive button.
  box.focus();

  modal.addEventListener('keydown', trapTab);
}

export function closeModal() {
  const modal = document.getElementById('modal');
  if (!modal.classList.contains('show')) return;

  modal.removeEventListener('keydown', trapTab);
  modal.classList.remove('show');
  document.body.style.overflow = '';

  const back = modalReturnFocus;
  modalReturnFocus = null;
  if (back && document.contains(back) && typeof back.focus === 'function') {
    back.focus();
  }
}

// Only act when a dialog is actually open, so Escape elsewhere on the page isn't
// silently swallowed and focus isn't restored to a stale element.
document.addEventListener('keydown', e => {
  if (e.key === 'Escape' && document.getElementById('modal').classList.contains('show')) {
    closeModal();
  }
});
// How long a toast stays up. The old flat 2.6s was fine for "Saved" and far too
// short to read a sentence, which is the WCAG 2.2.1 concern with timed content
// that can't be extended. Scale with length, and hold errors longer because they
// usually require the reader to go and do something about them.
const toastHoldMs = (msg, assertive) =>
  Math.min(12000, Math.max(assertive ? 6000 : 2600, msg.length * 60));

function showToast(msg, assertive) {
  const t = document.getElementById('toast');
  const body = document.getElementById('toastMsg');

  // Politeness is set before the text lands, so the region is already in the
  // right mode when the mutation is observed.
  t.setAttribute('role', assertive ? 'alert' : 'status');
  t.setAttribute('aria-live', assertive ? 'assertive' : 'polite');

  // Clear first, then write on the next frame. A live region only announces on
  // change, so toasting the identical message twice -- clicking a validation
  // button again, say -- would otherwise be silent the second time.
  body.textContent = '';
  requestAnimationFrame(() => { body.textContent = msg; });

  t.classList.add('show');
  clearTimeout(t._h);
  t._h = setTimeout(() => t.classList.remove('show'), toastHoldMs(msg, assertive));
}

// Confirmations and neutral notices: announced politely, when the reader is idle.
export function toast(msg) { showToast(msg, false); }

// Failures: announced assertively and held longer. Used by the catch blocks that
// surface a thrown message to the user.
export function toastError(msg) { showToast(msg, true); }

// ── CSV EXPORT ───────────────────────────────────────────────────────
// Mirrors src/Csv.php — see the reasoning there. Quoting alone doesn't help: a
// correctly quoted "=1+1" is still evaluated when the file is opened. A plain
// negative number is left alone so amount columns stay numeric.
const FORMULA_LEAD = /^[=+\-@\t\r]/;
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

function csvCell(value) {
  const s = String(value ?? '');
  return FORMULA_LEAD.test(s) && !PLAIN_NUMBER.test(s) ? "'" + s : s;
}

export function exportCSV(filename, headers, rows) {
  const q = v => '"' + csvCell(v).replace(/"/g, '""') + '"';
  const csv = [headers.map(q).join(','), ...rows.map(r => r.map(q).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename;
  a.click();
  toast('Exported ' + filename);
}

// ── SHARED CATALOG ICONS ─────────────────────────────────────────────
// Small icon-button glyphs shared by every admin domain with a
// duplicate/archive/restore catalog pattern (Merch Store, Summer Workshops,
// Classes) — one definition instead of the same three SVGs pasted per domain.
export const ICON_COPY = `<svg aria-hidden="true" viewBox="0 0 16 16"><rect x="5.5" y="5.5" width="8" height="8" rx="1.5"/><path d="M3.5 10.5h-1a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v1"/></svg>`;
export const ICON_ARCHIVE = `<svg aria-hidden="true" viewBox="0 0 16 16"><rect x="2" y="3" width="12" height="3" rx="1"/><path d="M3 6v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6"/><path d="M6.5 9h3"/></svg>`;
export const ICON_RESTORE = `<svg aria-hidden="true" viewBox="0 0 16 16"><path d="M2 10v3a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-3"/><path d="M8 2v7M5 6l3-3 3 3"/></svg>`;

Object.assign(window, { openModal, closeModal, toast, toastError, exportCSV });
